"use client";

import * as React from "react";
import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Globe, Clock, Calendar as CalendarIcon, AlertCircle } from "lucide-react";
import WaitlistJoinForm from "./WaitlistJoinForm";
import {
  generateTimeSlots,
  fetchBookedSlots,
  getBusinessSettings,
  getBusinessIdFromProviderId,
  getBusinessTimezone
} from '@/utils/availabilityCore';

export interface DateTimeSelectionStepProps {
  serviceDuration: number;
  selectedDateTime: {
    date: Date;
    time: string;
  } | null;
  onSelectDateTime: (value: {
    date: Date;
    time: string;
  } | null) => void;
  navigationDirection?: 'forward' | 'back';
  businessId?: string;
  serviceId?: string;
  serviceName?: string;
  providerId?: string;
}

export default function DateTimeSelectionStep({
  serviceDuration,
  selectedDateTime,
  onSelectDateTime,
  navigationDirection = 'forward',
  businessId,
  serviceId,
  serviceName,
  providerId
}: DateTimeSelectionStepProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(selectedDateTime?.date);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(selectedDateTime?.time);
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [businessTimezone, setBusinessTimezone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Race condition guard: persistent counter across effect runs
  const requestIdRef = useRef(0);

  // Skip sync flag: prevents clearing our own state when we notify parent
  const skipNextSyncRef = useRef(false);

  // Sync internal state when parent prop changes (one-way: parent → child only)
  useEffect(() => {
    // Skip sync if we just notified parent (avoid clearing our own change)
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    if (!selectedDateTime) {
      setSelectedDate(undefined);
      setSelectedTime(undefined);
      return;
    }

    setSelectedDate(selectedDateTime.date);
    setSelectedTime(selectedDateTime.time);
  }, [selectedDateTime]);

  // Fetch slots when date changes
  useEffect(() => {
    // Race condition guard: capture current request ID from shared ref
    const currentRequest = ++requestIdRef.current;

    async function loadSlotsForDate() {
      if (!selectedDate) {
        // Clear slots when date not selected (not an error state)
        setAvailableSlots([]);
        setBookedSlots([]);
        setLoading(false);
        return;
      }

      // Resolve businessId: use provided or fetch from providerId
      let resolvedBusinessId = businessId;
      if (!resolvedBusinessId && providerId) {
        resolvedBusinessId = await getBusinessIdFromProviderId(providerId);
      }

      if (!resolvedBusinessId) {
        // No way to determine business context - fail closed
        setAvailableSlots([]);
        setBookedSlots([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Get business settings and timezone in parallel
        const [settings, bizTimezone] = await Promise.all([
          getBusinessSettings(resolvedBusinessId),
          getBusinessTimezone(resolvedBusinessId)
        ]);

        // Persist business timezone so the label can display it
        const resolvedBizTimezone = bizTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        setBusinessTimezone(resolvedBizTimezone);

        // Generate all available slots for this date
        const allSlots = await generateTimeSlots(selectedDate, resolvedBusinessId, settings, resolvedBizTimezone);

        // Fetch booked slots
        const booked = await fetchBookedSlots({
          businessId: resolvedBusinessId,
          providerId,
          date: selectedDate,
          slotInterval: settings.slot_interval_minutes
        });

        // Discard stale results if dependencies changed during async operations
        if (currentRequest !== requestIdRef.current) return;

        setAvailableSlots(allSlots);
        setBookedSlots(booked);
      } catch (error) {
        console.error('Error loading slots:', error);
        // Discard stale errors if dependencies changed
        if (currentRequest !== requestIdRef.current) return;
        setAvailableSlots([]);
        setBookedSlots([]);
      } finally {
        // Only clear loading if this request is still current
        // (prevents stale requests from clearing loading while newer requests are pending)
        if (currentRequest === requestIdRef.current) {
          setLoading(false);
        }
      }
    }

    loadSlotsForDate();
  }, [selectedDate, businessId, providerId]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedTime(undefined); // Reset time when date changes

    // Notify parent and skip next sync to preserve our state
    skipNextSyncRef.current = true;
    onSelectDateTime(null);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      skipNextSyncRef.current = true;
      onSelectDateTime({ date: selectedDate, time });
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return <section className="space-y-8">
      <header>
        <h2 className="text-3xl font-bold tracking-tight">Pick a Date & Time</h2>
        <p className="text-muted-foreground mt-2">Select an available day and time slot for your session.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.5
      }}>
          <div className="p-1 border rounded-xl bg-muted/30">
            <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} disabled={date => date < today} className="p-3" classNames={{
            day_selected: "bg-primary text-primary-foreground hover:bg-primary/90 focus:bg-primary/90",
            day_today: "bg-accent text-accent-foreground"
          }} />
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="min-h-[300px]">
          <div className="space-y-4">
            <h3 className="font-semibold text-lg flex items-center">
              <Clock className="mr-2 h-5 w-5 text-primary" />
              Available Time Slots
            </h3>
            <p className="text-xs text-muted-foreground -mt-2">
              Times shown in {businessTimezone.replace(/_/g, ' ')}
            </p>

            <AnimatePresence mode="wait">
              {selectedDate ? (
                loading ? (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4"></div>
                    <p className="text-muted-foreground">Loading availability...</p>
                  </motion.div>
                ) : availableSlots.length > 0 ? (
                  <motion.div key={selectedDate.toString()} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    duration: 0.3
                  }}>
                    <p className="text-sm text-muted-foreground mb-4">
                      Showing times for {selectedDate.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {availableSlots.map(time => (
                        <Button
                          key={time}
                          variant={selectedTime === time ? "default" : "outline"}
                          onClick={() => handleTimeSelect(time)}
                          disabled={bookedSlots.includes(time)}
                          className={cn(
                            "transition-all duration-200 h-12 text-sm font-medium whitespace-nowrap",
                            selectedTime === time
                              ? "bg-primary text-primary-foreground shadow-lg scale-105"
                              : bookedSlots.includes(time)
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-accent hover:text-accent-foreground hover:scale-102"
                          )}
                        >
                          {time}
                        </Button>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key="no-slots" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="flex flex-col items-center justify-center text-center py-8 border-2 border-dashed rounded-xl h-[200px] bg-muted/30">
                    <AlertCircle className="h-10 w-10 mb-4 text-orange-500" />
                    <p className="font-medium text-foreground">No slots available</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      for {selectedDate.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    {(() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const selectedDateOnly = new Date(selectedDate);
                      selectedDateOnly.setHours(0, 0, 0, 0);
                      const isSameDay = selectedDateOnly.getTime() === today.getTime();

                      if (isSameDay) {
                        return (
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground mb-2">
                              Same-day waitlist is not available.
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Please try a different date or contact the business directly.
                            </p>
                          </div>
                        );
                      }

                      return (
                        <Button
                          onClick={() => setShowWaitlistForm(true)}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                        >
                          Join Waitlist
                        </Button>
                      );
                    })()}
                  </motion.div>
                )
              ) : (
                <motion.div key="placeholder" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="flex flex-col items-center justify-center text-center text-muted-foreground py-8 border-2 border-dashed rounded-xl h-[200px]">
                  <CalendarIcon className="h-10 w-10 mb-4 text-muted-foreground/50" />
                  <p className="font-medium">Please select a date</p>
                  <p className="text-sm">to see available time slots.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      <div className="flex items-center gap-2 pt-4 border-t">
        <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm text-muted-foreground">
          All times shown in {businessTimezone ? businessTimezone.replace(/_/g, " ") : "business timezone"}
        </span>
      </div>

      {/* Waitlist Form Dialog */}
      <WaitlistJoinForm
        open={showWaitlistForm}
        onOpenChange={setShowWaitlistForm}
        businessId={businessId || "demo-business-id"}
        serviceId={serviceId}
        serviceName={serviceName}
        requestedDate={selectedDate ?
          `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
          : undefined
        }
      />
    </section>;
}
